<script setup lang="ts">
import {VsgSwitch} from '@jack-henry/jha-vue-components'
import '@banno/jha-wc/src/forms/jha-form-switch/jha-form-switch.js'
import '@banno/jha-wc/src/icons/jha-icon-information.js'
import {computed, onMounted, ref, watch} from 'vue'
import {useLDFlag} from 'launchdarkly-vue-client-sdk'
import {$inj, $injByInterface} from '@/common/decorators/depinject'
import BltModalDialog from '@/common/components/bltDialog/bltModalDialog.vue'
import LanguageFactory from '@/common/services/Language/LanguageFactory'
import {OpenLanguageKeysConstant} from '@/common/constant/OpenLanguageKeysConstant'
import {BranchLanguageKeysConstant} from '@/branchmanager/app/constant/BranchLanguageKeysConstant'
import ApplicantIndicatorFactory, {
  type ApplicantIndicatorDto
} from '@/common/services/Applicant/ApplicantIndicatorFactory'
import type IWorkspaceStore from '@/common/services/Workspace/IWorkspaceStore'

const props = withDefaults(
  defineProps<{
    /** Which channel the toggle is rendered in; decides which verbiage keys are used. */
    channel: 'open' | 'branch'
    disabled?: boolean
    hideReportableToggle?: boolean
  }>(),
  {
    disabled: false,
    hideReportableToggle: false
  }
)

const verbiageKeys = computed(() =>
  props.channel === 'branch'
    ? {
        usCitizenLabel: BranchLanguageKeysConstant.BRANCH_PERSON_INPUT_IS_US_CITIZEN,
        reportableLabel: BranchLanguageKeysConstant.BRANCH_PERSON_INPUT_IS_REPORTABLE,
        helperLabel: BranchLanguageKeysConstant.BRANCH_PERSON_CITIZEN_HELPER_LABEL,
        helperText: BranchLanguageKeysConstant.BRANCH_PERSON_CITIZEN_HELPER_TEXT
      }
    : {
        usCitizenLabel: OpenLanguageKeysConstant.OPEN_PERSONINFO_LABEL_IS_US_CITIZEN,
        reportableLabel: OpenLanguageKeysConstant.OPEN_PERSONINFO_LABEL_IS_REPORTABLE,
        helperLabel: OpenLanguageKeysConstant.OPEN_PERSONINFO_CITIZEN_HELPER_LABEL,
        helperText: OpenLanguageKeysConstant.OPEN_PERSONINFO_CITIZEN_HELPER_TEXT
      }
)

const emit = defineEmits<{
  (event: 'update:isUsCitizen', value: boolean): void
}>()

const applicantIndicatorFactory = $inj(ApplicantIndicatorFactory)
const languageFactory = $inj(LanguageFactory)
const workspaceStore = $injByInterface<IWorkspaceStore>('IWorkspaceStore')
const foreignApplicantsLdFlag = useLDFlag('oa-foreign-applicants', false)

const isForeignPerson = ref(false)
const isReportablePerson = ref(false)
const foreignStatusIndicatorId = ref<string | null>(null)
const indicatorsHydrated = ref(false)
const saveInFlight = ref(false)
const hydrateInFlight = ref(false)
const lastSyncedIndicatorValue = ref<Record<'foreign' | 'reportable', boolean>>({
  foreign: false,
  reportable: false
})

// The UI asks "Are you a U.S. person?" while the backend stores `foreign`.
// The switch/label are the inverse of `isForeignPerson`; state and API payloads are unchanged.
const isUsCitizen = computed(() => !isForeignPerson.value)
const foreignYesNoLabel = computed(() => (isUsCitizen.value ? 'Yes' : 'No'))
const reportableYesNoLabel = computed(() => (isReportablePerson.value ? 'Yes' : 'No'))
const interactionLocked = computed(() => saveInFlight.value || hydrateInFlight.value)
const switchesDisabled = computed(() => props.disabled || interactionLocked.value)

const helperLabel = ref('')
const helperText = ref('')
// The verbiage can contain line breaks; render each one as its own paragraph.
const helperParagraphs = computed(() =>
  helperText.value
    .split(/\r?\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
)
const showHelperModal = ref(false)

async function loadHelperVerbiage() {
  if (!foreignApplicantsLdFlag.value) {
    return
  }
  try {
    const [label, text] = await Promise.all([
      languageFactory.get(verbiageKeys.value.helperLabel),
      languageFactory.get(verbiageKeys.value.helperText)
    ])
    helperLabel.value = label ?? ''
    helperText.value = text ?? ''
  } catch (error) {
    console.error('Failed to load citizen helper verbiage:', error)
  }
}

/** Opens the modal that explains the U.S. citizen / U.S. person question. */
const onClick = () => {
  showHelperModal.value = true
}

const closeHelperModal = () => {
  showHelperModal.value = false
}

const onSwitchKeydown = (event: KeyboardEvent) => {
  if (!switchesDisabled.value) {
    return
  }
  if (event.key === ' ' || event.key === 'Enter') {
    event.preventDefault()
    event.stopPropagation()
  }
}

const onForeignChange = (event: Event & {detail?: {checked?: boolean}}) => {
  if (switchesDisabled.value) {
    return
  }
  // Switch "checked" means U.S. person, so foreign is the inverse.
  isForeignPerson.value = !event.detail?.checked
  void persistIndicator('foreign')
}

const onReportableChange = (event: Event & {detail?: {checked?: boolean}}) => {
  if (switchesDisabled.value) {
    return
  }
  isReportablePerson.value = !!event.detail?.checked
  void persistIndicator('reportable')
}

function isNotFoundResponse(error: unknown): boolean {
  return (error as {status?: number} | undefined)?.status === 404
}

type IndicatorField = 'foreign' | 'reportable'

function applyIndicatorState(
  indicators: ApplicantIndicatorDto | null,
  options: {fieldsToApply?: IndicatorField[]} = {}
) {
  const fieldsToApply = options.fieldsToApply ?? ['foreign', 'reportable']

  foreignStatusIndicatorId.value = indicators?.applicantForeignStatusIndicatorId ?? null

  if (fieldsToApply.includes('foreign')) {
    isForeignPerson.value = indicators?.foreign ?? false
    lastSyncedIndicatorValue.value.foreign = indicators?.foreign ?? false
  }
  if (fieldsToApply.includes('reportable')) {
    isReportablePerson.value = indicators?.reportable ?? false
    lastSyncedIndicatorValue.value.reportable = indicators?.reportable ?? false
  }
}

/** Fetch indicator state from the server, even after hydration. */
async function syncIndicatorsFromServer(
  options: {fieldsToApply?: IndicatorField[]} = {}
): Promise<boolean> {
  if (!workspaceStore.applicant) {
    return false
  }

  const workspaceUUID = workspaceStore.workspaceUUID
  const applicantId = workspaceStore.applicant.applicantId

  try {
    const indicators = await applicantIndicatorFactory.getIndicators(workspaceUUID, applicantId)

    // payload is null until the first POST — treat as "no row yet".
    applyIndicatorState(indicators, {fieldsToApply: options.fieldsToApply})
    return true
  } catch (error) {
    if (isNotFoundResponse(error)) {
      // 404 -> treat as unset.
      applyIndicatorState(null, {fieldsToApply: options.fieldsToApply})
      return true
    }
    throw error
  }
}

async function hydrateApplicantIndicators() {
  if (
    !foreignApplicantsLdFlag.value ||
    !workspaceStore.applicant ||
    indicatorsHydrated.value ||
    hydrateInFlight.value ||
    saveInFlight.value
  ) {
    return
  }
  hydrateInFlight.value = true
  try {
    if (await syncIndicatorsFromServer()) {
      indicatorsHydrated.value = true
    }
  } catch (error) {
    console.error('Failed to hydrate applicant indicators:', error)
    // Keep unhydrated so transient failures can retry.
  } finally {
    hydrateInFlight.value = false
  }
}

/** Write one field. POST first, then PUT /{id}; recover 400/409 by GET + PUT. */
async function writeIndicator(
  workspaceUUID: string,
  applicantId: number,
  field: 'foreign' | 'reportable',
  value: boolean
): Promise<ApplicantIndicatorDto | null> {
  const patch = {[field]: value}

  if (!foreignStatusIndicatorId.value) {
    try {
      return await applicantIndicatorFactory.createIndicators(workspaceUUID, applicantId, patch)
    } catch (error) {
      // Already exists (400/409) — recover by fetching the existing id.
      const status = (error as {status?: number} | undefined)?.status
      if (status !== 400 && status !== 409) {
        throw error
      }
      const existing = await applicantIndicatorFactory.getIndicators(workspaceUUID, applicantId)
      const existingId = existing?.applicantForeignStatusIndicatorId
      if (!existingId) {
        throw error
      }
      foreignStatusIndicatorId.value = existingId
      return await applicantIndicatorFactory.updateIndicators(workspaceUUID, applicantId, existingId, patch)
    }
  }

  return await applicantIndicatorFactory.updateIndicators(
    workspaceUUID,
    applicantId,
    foreignStatusIndicatorId.value,
    patch
  )
}

/** Persist one field while both toggles are locked. */
function persistIndicator(field: IndicatorField) {
  if (!workspaceStore.applicant || saveInFlight.value) {
    return Promise.resolve()
  }

  const workspaceUUID = workspaceStore.workspaceUUID
  const applicantId = workspaceStore.applicant.applicantId
  const value = field === 'foreign' ? isForeignPerson.value : isReportablePerson.value
  let shouldHydrateAfterSettle = false
  saveInFlight.value = true

  const rollbackField = () => {
    if (field === 'foreign') {
      isForeignPerson.value = lastSyncedIndicatorValue.value.foreign
      return
    }
    isReportablePerson.value = lastSyncedIndicatorValue.value.reportable
  }

  return writeIndicator(workspaceUUID, applicantId, field, value)
    .then((result) => {
      if (field === 'foreign') {
        const syncedValue = result?.foreign ?? value
        isForeignPerson.value = syncedValue
        lastSyncedIndicatorValue.value.foreign = syncedValue
      }
      if (field === 'reportable') {
        const syncedValue = result?.reportable ?? value
        isReportablePerson.value = syncedValue
        lastSyncedIndicatorValue.value.reportable = syncedValue
      }

      // updateIndicators can return full state.
      if (result?.foreign !== undefined) {
        isForeignPerson.value = result.foreign
        lastSyncedIndicatorValue.value.foreign = result.foreign
      }
      if (result?.reportable !== undefined) {
        isReportablePerson.value = result.reportable
        lastSyncedIndicatorValue.value.reportable = result.reportable
      }

      if (result?.applicantForeignStatusIndicatorId) {
        foreignStatusIndicatorId.value = result.applicantForeignStatusIndicatorId
      }
    })
    .catch(() => {
      shouldHydrateAfterSettle = true
      return syncIndicatorsFromServer({fieldsToApply: [field]})
        .then((reSyncApplied) => {
          if (!reSyncApplied) {
            rollbackField()
          }
        })
        .catch((reSyncError) => {
          console.error('Failed to re-sync applicant indicators after write error:', reSyncError)
          rollbackField()
        })
    })
    .finally(() => {
      saveInFlight.value = false
      if (shouldHydrateAfterSettle && !indicatorsHydrated.value) {
        void hydrateApplicantIndicators()
      }
    })
}

onMounted(() => {
  void hydrateApplicantIndicators()
  void loadHelperVerbiage()
})

// Let parents track U.S. citizen status (e.g. to swap SSN for FTIN/ITIN fields).
// The API still stores `foreign`; this only exposes the inverse to parents.
watch(
  isUsCitizen,
  (value) => {
    emit('update:isUsCitizen', value)
  },
  {immediate: true}
)
</script>

<template>
  <div v-if="foreignApplicantsLdFlag">
    <div
      class="toggle-row"
      :class="{locked: interactionLocked, 'single-toggle': hideReportableToggle}"
      :aria-busy="interactionLocked ? 'true' : 'false'">
      <div class="us-citizen-column">
        <div
          class="foreign-applicant-toggle"
          data-cy="us-citizen-toggle">
          <div class="content">
            <span
              class="heading"
              v-blt-ui-text="verbiageKeys.usCitizenLabel">
            </span>
          </div>

          <div class="switch-section">
            <vsg-switch
              :checked="isUsCitizen"
              :disabled="disabled"
              @keydown.capture="onSwitchKeydown"
              @change="onForeignChange"
              data-cy="us-citizen-toggle-switch">
              <!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
              <span
                slot="right-label"
                class="switch-label"
                data-cy="us-citizen-toggle-value">
                {{ foreignYesNoLabel }}
              </span>
            </vsg-switch>
          </div>
        </div>

        <div class="helper-row">
          <a
            v-if="helperLabel"
            href="#"
            class="helper-link pointer-cursor"
            aria-haspopup="dialog"
            data-cy="us-citizen-helper-link"
            @click.prevent="onClick">
            <jha-icon-information x-small></jha-icon-information>&nbsp;{{ helperLabel }}
          </a>
          <button
            v-else
            type="button"
            class="helper-icon-button pointer-cursor"
            aria-label="More information about U.S. person status"
            aria-haspopup="dialog"
            data-cy="us-citizen-helper-icon"
            @click="onClick">
            <jha-icon-information
              small
              class="secondary-fill-color" />
          </button>
        </div>
      </div>

      <div
        v-if="!hideReportableToggle"
        class="foreign-applicant-toggle"
        data-cy="reportable-applicant-toggle">
        <div class="content reportable-content">
          <span
            class="heading"
            v-blt-ui-text="verbiageKeys.reportableLabel">
          </span>
        </div>

        <div class="switch-section">
          <vsg-switch
            :checked="isReportablePerson"
            :disabled="disabled"
            @keydown.capture="onSwitchKeydown"
            @change="onReportableChange"
            data-cy="reportable-applicant-toggle-switch">
            <span
              slot="right-label"
              class="switch-label"
              data-cy="reportable-applicant-toggle-value">
              {{ reportableYesNoLabel }}
            </span>
          </vsg-switch>
        </div>
      </div>
    </div>

    <blt-modal-dialog
      :showDialog="showHelperModal"
      :title="helperLabel"
      type="normal-dialog"
      show-confirm
      confirm-label="OK"
      preventAutoClose
      @cancel="closeHelperModal"
      @confirm="closeHelperModal">
      <template #dialog-content>
        <div
          class="helper-text"
          data-cy="us-citizen-helper-text">
          <p
            v-for="(paragraph, index) in helperParagraphs"
            :key="index">
            {{ paragraph }}
          </p>
        </div>
      </template>
    </blt-modal-dialog>
  </div>
</template>

<style scoped>
.toggle-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: var(--jh-space-300);
  row-gap: 0;
}

.toggle-row.single-toggle {
  grid-template-columns: minmax(0, 1fr);
}

.us-citizen-column {
  display: contents;
}

.us-citizen-column > .foreign-applicant-toggle {
  grid-column: 1;
  grid-row: 1;
}

.us-citizen-column > .helper-row {
  grid-column: 1;
  grid-row: 2;
}

.toggle-row > .foreign-applicant-toggle {
  grid-column: 2;
  grid-row: 1;
}

.toggle-row.locked {
  pointer-events: none;
}

.foreign-applicant-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--jh-space-400);
  border: 1px solid var(--divider-alternative-color);
  border-radius: var(--jh-space-200);
  padding: var(--jh-space-100) var(--jh-space-400);
}

.content {
  flex: 1;
}

.heading {
  margin: 0;
  display: block;
  color: var(--body-text-primary-color);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.pointer-cursor {
  cursor: pointer;
}

.helper-text {
  text-align: left;
}

.helper-row {
  display: flex;
  align-items: center;
  margin-top: var(--jh-space-100);
  padding-left: var(--jh-space-400);
  pointer-events: auto;
}

.helper-text p {
  margin: 0 0 var(--jh-space-300);
}

.helper-text p:last-child {
  margin-bottom: 0;
}

button.helper-icon-button {
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
}

a.helper-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  font-size: var(--jh-font-size-300);
  color: var(--jh-color-content-brand-enabled);
  fill: var(--jh-color-content-brand-enabled);
}

a.helper-link > * {
  fill: var(--jh-color-content-brand-enabled);
}

.switch-section {
  display: flex;
  align-items: center;
  min-height: 56px;
}

.switch-label {
  min-width: 24px;
  text-align: right;
}

.reportable-content {
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .toggle-row {
    grid-template-columns: 1fr;
  }

  .toggle-row > .foreign-applicant-toggle {
    grid-column: 1;
    grid-row: 3;
    margin-top: var(--jh-space-300);
  }

  .foreign-applicant-toggle {
    align-items: flex-start;
    flex-direction: column;
  }

  .switch-section {
    justify-content: space-between;
    width: 100%;
  }
}
</style>

